/**
 * Notification Permission Banner
 *
 * Shows a banner prompting the user to enable browser notifications.
 * Dismisses automatically when permission is granted or denied.
 * Can be dismissed manually and won't show again for that session.
 */

import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBrowserNotifications } from '@/hooks/use-browser-notifications';

interface NotificationPermissionBannerProps {
  /**
   * Optional className for styling
   */
  className?: string;
}

export function NotificationPermissionBanner({ className }: NotificationPermissionBannerProps) {
  const {
    isSupported,
    needsPermission,
    isDenied,
    requestPermission,
  } = useBrowserNotifications();

  const [isDismissed, setIsDismissed] = useState(() => {
    // Check if user dismissed the banner in this session
    return sessionStorage.getItem('notification-banner-dismissed') === 'true';
  });

  const [isLoading, setIsLoading] = useState(false);

  // Don't show if not supported, already decided, or dismissed
  if (!isSupported || !needsPermission || isDenied || isDismissed) {
    return null;
  }

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      await requestPermission();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('notification-banner-dismissed', 'true');
  };

  return (
    <div
      className={`
        fixed top-4 left-4 z-50
        max-w-sm p-4
        bg-card
        border border-border
        rounded-lg shadow-lg
        animate-in slide-in-from-left-5 fade-in duration-300
        ${className || ''}
      `}
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="flex-shrink-0 p-2 bg-primary/10 rounded-full">
          <Bell className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-foreground">
            Enable Notifications
          </h4>
          <p className="mt-1 text-sm text-muted-foreground">
            Get notified about new messages, bookings, and job updates even when you're on another tab.
          </p>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDismiss}
        >
          Not now
        </Button>
        <Button
          size="sm"
          onClick={handleEnable}
          disabled={isLoading}
        >
          {isLoading ? 'Enabling...' : 'Enable'}
        </Button>
      </div>
    </div>
  );
}
